/**
 * The downsize, as one applyable event: sell the home, buy the new one, make the contributions and
 * gifts, deploy the residue, bank the rest.
 *
 * THE one implementation, moved here from the frontend's `supportAtHomeFactFindMutators.tsx` so the
 * PROJECTION ENGINE can perform it on a dated day against live balances — the same journey the RAD
 * funding sale made. The frontend re-exports this function, so the Support at Home and Downsizer
 * modules keep calling exactly what they always called; only the module it lives in moved.
 *
 * A NOTE ON PRICES UNDER DEFERRAL. `details.salePrice` and `details.newHomePurchasePrice` are the
 * adviser's entered figures and are used AS ENTERED whenever the event happens — the engine does not
 * grow them to the event date. That is deliberate: an adviser modelling a sale two years out enters
 * the price they expect to achieve then, and silently indexing their number would second-guess it.
 * The step this can create in net worth (selling above or below the home's carried value) is real
 * economics, not an error.
 */

import { recordDatedGift } from '../giftingDeprivation.ts';
import { applyDownsizerContributionsToAssets } from '../downsizerContributionUtils.ts';
import { applyResidualDeployment, type ResidualDeployment } from '../residualDeployment.ts';
import { markAbpFundedAcquisitionAfterLiquidation } from '../abpProjectionUtils.ts';
import { debitFromPersonalBankFirst, findAssetInFactFind, parseRadFundingAssetKey, resolveRadFundingEntry } from './entityFundingUtils.ts';
import type { DownsizeDetails } from '../strategyOptionsTypes.ts';
import type { FactFind, FactFindAsset, FactFindAssetMap } from '../factFindTypes.ts';

/** A dated downsize, as `strategyOptions.downsizeEvent` carries it to the engine. */
export interface DownsizeEvent {
    /** When the sale settles and the plan executes, `YYYY-MM-DD`. */
    date: string;
    details: Partial<DownsizeDetails>;
}

/** What `liquidateChosenAssetsForDownsize` raised, and the fact-find it raised it from. */
interface LiquidationResult {
    factFind: FactFind;
    net: number;
    gross: number;
    cgt: number;
}

/**
 * Sells the client's chosen assets to cover a buy-up shortfall — the same arithmetic the RAD funding
 * sale uses (same key parsing, same CGT resolution, same per-asset clamp), ported with the mutator so
 * the engine can reach it.
 */
function liquidateChosenAssetsForDownsize(
    factFind: FactFind,
    fundingMap: Record<string, number> | null | undefined,
    cgtOverrides: Record<string, number> = {},
): LiquidationResult {
    const modified = structuredClone(factFind);
    if (!fundingMap) return { factFind: modified, net: 0, gross: 0, cgt: 0 };
    let net = 0, gross = 0, cgt = 0;
    Object.entries(fundingMap).forEach(([assetKey, amount]) => {
        const parsed = parseRadFundingAssetKey(assetKey);
        if (!parsed) return;
        const entry = resolveRadFundingEntry(factFind, assetKey, amount, cgtOverrides);
        if (entry.gross <= 0) return;
        const located = findAssetInFactFind(modified, parsed.assetType, parsed.assetId);
        if (!located) return;
        located.asset.value = (located.asset.value || 0) - entry.gross;
        if (parsed.assetType === 'accountBasedPensions') {
            markAbpFundedAcquisitionAfterLiquidation(located.asset as FactFindAsset, modified, 1);
        }
        net += entry.net; gross += entry.gross; cgt += entry.cgt;
    });
    return { factFind: modified, net, gross, cgt };
}

/** What the event did — the numbers a narrative or a test needs to describe it. */
export interface DownsizeEventOutcome {
    factFind: FactFind;
    salePrice: number;
    sellingCosts: number;
    newHomePurchase: number;
    /** Duty on the new home, taken off the proceeds beside the selling costs; 0 where none was given. */
    stampDuty: number;
    /** Sale residue that reached the bank after the purchase, contributions, gifts and deployment. */
    surplusToBank: number;
    /** Weekly rent the household now pays, where the plan rents instead of buying; 0 otherwise. */
    rentPerWeek: number;
}

/**
 * Applies the downsize to `factFind`, returning a NEW fact-find — the caller's is never touched.
 *
 * Ported line-for-line from the frontend mutator (see module header); the behaviour is pinned by the
 * suites that already covered it there, plus the dated-event suite. `eventDate` times the gifting
 * record: a gift made on the event day belongs to the financial year the EVENT falls in, not the year
 * the projection was set up — the five-year deprivation clock runs from when the gift is really made.
 */
export function applyDownsizeEventToFactFind(
    factFind: FactFind,
    details: Partial<DownsizeDetails> = {},
    eventDate: Date = new Date(),
): DownsizeEventOutcome {
    const modified: FactFind = structuredClone(factFind);
    const currentHomeValue = Number(factFind.home?.value) || 0;
    const salePrice = Number(details.salePrice ?? currentHomeValue) || currentHomeValue;
    const sellingCosts = Number(details.sellingCosts) || 0;
    // Duty on the new home comes off the proceeds like the selling costs do — a sunk cost of the
    // changeover that the model never charged before 23 Sep 2026 (the owner had been carrying it in
    // his head). Zero where no state was chosen: no state, no guess.
    const stampDuty = Number(details.stampDuty) || 0;

    let remainingProceeds = Math.max(0, salePrice - sellingCosts - stampDuty);
    if (!modified.assets) modified.assets = {};

    const newHomePurchase = Math.max(0, Number(details.newHomePurchasePrice) || 0);
    if (modified.home) {
        modified.home.value = newHomePurchase > 0 ? newHomePurchase : 0;
    } else if (newHomePurchase > 0) {
        modified.home = { value: newHomePurchase };
    }
    remainingProceeds -= newHomePurchase;

    /*
     * SELL AND RENT (2 Oct 2026). A plan that buys no new home and says so becomes a renting
     * household: both members are non-homeowners from the day of sale (the higher assets-test
     * limit, and Rent Assistance on the rent they now pay), and the rent is charged from then
     * on — as the household's housing cost (assessment/expenses.ts `calculateHousingCosts`, which
     * reads `rent.rentAmount` for a renting household and escalates it with CPI; until 8 Oct 2026
     * a year of it was added to living expenses instead, which double-charged it once housing
     * costs became a line of their own).
     * Before this the engine zeroed the home and left the residency, the thresholds and the
     * budget as they were, so renting cost nothing and paid no assistance (owner-caught).
     *
     * Gated on `rentInstead`, never on a nil price alone: the Support at Home engine's downsize
     * candidates have always carried a target price and must not change meaning.
     */
    const rentPerWeek = details.rentInstead === true ? Math.max(0, Number(details.rentPerWeek) || 0) : 0;
    if (details.rentInstead === true && newHomePurchase <= 0) {
        if (modified.client) modified.client.currentResidency = 'renting';
        if (factFind.adviceFor === 'couple' && modified.partner) modified.partner.currentResidency = 'renting';
        if (rentPerWeek > 0) {
            modified.rent = {
                ...(modified.rent || {}),
                rentAmount: rentPerWeek,
                landlordType: modified.rent?.landlordType || 'private',
                sharedAccommodation: modified.rent?.sharedAccommodation ?? false,
            };
        }
    }

    // "Buying up": the new home costs MORE than the net sale proceeds — fund the gap from the client's
    // chosen assets, then the bank, so the exempt home never rises without an asset being spent.
    if (remainingProceeds < 0) {
        let shortfall = -remainingProceeds;
        remainingProceeds = 0;
        const buyUpFunding = details.buyUpFunding as Record<string, number> | undefined;
        if (buyUpFunding && Object.keys(buyUpFunding).length > 0) {
            const liq = liquidateChosenAssetsForDownsize(modified, buyUpFunding, (details.buyUpCgtOverrides as Record<string, number>) || {});
            modified.assets = liq.factFind.assets;
            const cover = Math.min(liq.net, shortfall);
            shortfall -= cover;
            remainingProceeds += Math.max(0, liq.net - cover);
        }
        if (shortfall > 0) {
            debitFromPersonalBankFirst(modified.assets as FactFindAssetMap, shortfall);
        }
    }

    const addedAssets = Array.isArray(details.addedAssets) ? (details.addedAssets as Record<string, unknown>[]) : [];
    addedAssets.forEach((asset) => {
        const assetType = String(asset.type || 'bankAccounts');
        const assetValue = Math.max(0, Number(asset.value) || 0);
        if (assetValue <= 0) return;
        remainingProceeds -= assetValue;
        if (!modified.assets![assetType]) modified.assets![assetType] = [];
        const payload: FactFindAsset = {
            id: (asset.id as string | undefined) || `downsize-${assetType}-${Math.random().toString(36).slice(2, 10)}`,
            type: assetType as FactFindAsset['type'],
            description: (asset.description as string | undefined) || 'Downsize Allocation',
            value: assetValue,
            ownership: (asset.ownership as string | undefined) || (factFind.adviceFor === 'couple' ? 'joint' : 'client'),
        };
        if (asset.incomeRate !== undefined && asset.incomeRate !== '') payload.incomeRate = Number(asset.incomeRate) || 0;
        if (asset.growthRate !== undefined && asset.growthRate !== '') payload.growthRate = Number(asset.growthRate) || 0;
        if (asset.frankingCredit !== undefined && asset.frankingCredit !== '') payload.frankingCredit = Number(asset.frankingCredit) || 0;
        if (asset.costBase !== undefined && asset.costBase !== '') payload.costBase = Number(asset.costBase) || 0;
        modified.assets![assetType]!.push(payload);
    });

    const clientDownsizer = Math.max(0, Number(details.downsizerContribution) || 0);
    const partnerDownsizer = Math.max(0, Number(details.partnerDownsizerContribution) || 0);
    applyDownsizerContributionsToAssets(modified.assets as FactFindAssetMap, factFind, details, {
        assetId: 'downsizer-client',
        partnerAssetId: 'downsizer-partner',
    });
    remainingProceeds -= clientDownsizer + partnerDownsizer;

    const giftingAmount = Math.max(0, Number(details.giftingAmount) || 0);
    if (giftingAmount > 0) {
        // The financial year the EVENT falls in — a deferred downsize's gift is made when the money
        // exists to give, and the deprivation clock runs from there.
        // Dated to the event, so the five years run from that day (24 Oct 2026).
        recordDatedGift(modified as never, giftingAmount, eventDate);
        remainingProceeds -= giftingAmount;
    }

    /*
     * FUNERAL BONDS FROM THE PROCEEDS.
     *
     * `DownsizeDetails` has declared this field all along and nothing on the downsize path read it,
     * so a plan that allocated money to a bond quietly left it in the bank — counted, deemed, and
     * reported as though the allocation had happened. Found while adding it as a Suggestion Engine
     * candidate (2026-08-27): the candidate would have been a silent no-op.
     *
     * It matters here more than almost anywhere: the downsizing trap IS that the home was exempt and
     * the money it becomes is not, and a funeral bond is the cheapest way to put part of that money
     * back outside the assets test. The same treatment the village and entity-IP-sale paths have
     * always given it.
     *
     * The published per-person limit is NOT enforced here. That is deliberate and matches gifting
     * above: this function executes what the plan says, and the callers that build a plan are where
     * the limit belongs — the engine reporting a bond larger than the exemption as fully exempt
     * would be a different bug, and one for the assessment to catch, not this.
     */
    const funeralBondPurchase = Math.max(0, Number(details.funeralBondPurchase) || 0);
    if (funeralBondPurchase > 0) {
        if (!modified.assets) modified.assets = {};
        if (!modified.assets.funeralBonds) modified.assets.funeralBonds = [];
        modified.assets.funeralBonds.push({
            id: 'downsize-funeral-bond',
            type: 'funeralBonds',
            description: 'Funeral bond (from home sale)',
            value: funeralBondPurchase,
            ownership: factFind.adviceFor === 'couple' ? 'joint' : 'client',
            income: 0,
            growthRate: 0,
        } as never);
        remainingProceeds -= funeralBondPurchase;
    }

    const debtRepayment = Math.max(0, Number(details.debtRepayment) || 0);
    const selectedDebtKey = details.selectedDebtKey as string | undefined;
    if (debtRepayment > 0 && selectedDebtKey && modified.liabilities) {
        const [liabilityType, liabilityId] = selectedDebtKey.split(':');
        const liabilityList = modified.liabilities[liabilityType!];
        if (Array.isArray(liabilityList)) {
            const target = liabilityList.find((item) => String(item.id) === String(liabilityId));
            if (target) {
                const currentBalance = Math.max(0, (target.balance ?? (target as { value?: number }).value ?? 0) as number);
                const newBalance = Math.max(0, currentBalance - debtRepayment);
                if (target.balance !== undefined) target.balance = newBalance;
                else (target as { value?: number }).value = newBalance;
            }
        }
        remainingProceeds -= debtRepayment;
    }

    if (details.deployment) {
        const deployed = applyResidualDeployment(modified, details.deployment as ResidualDeployment, {
            factFind,
            idPrefix: 'downsize-deploy',
            defaultOwnership: factFind.adviceFor === 'couple' ? 'joint' : 'client',
        });
        remainingProceeds -= deployed;
    }

    let surplusToBank = 0;
    if (remainingProceeds > 0) {
        surplusToBank = remainingProceeds;
        if (!modified.assets) modified.assets = {};
        if (!modified.assets.bankAccounts) modified.assets.bankAccounts = [];
        if (modified.assets.bankAccounts.length > 0) {
            const first = modified.assets.bankAccounts[0]!;
            first.value = (Number(first.value) || 0) + remainingProceeds;
        } else {
            modified.assets.bankAccounts.push({
                id: 'downsize-surplus',
                type: 'bankAccounts',
                description: 'Downsize Surplus',
                value: remainingProceeds,
                ownership: factFind.adviceFor === 'couple' ? 'joint' : 'client',
                incomeRate: 2,
                growthRate: 0,
            });
        }
    }

    return { factFind: modified, salePrice, sellingCosts, newHomePurchase, stampDuty, surplusToBank, rentPerWeek };
}
